/**
 * Bulk guide import — the list of past and running batches.
 *
 * A thin index onto the dashboards: it exists so a batch can be found again after the operator has
 * navigated away, which otherwise means keeping the batch id in a browser tab. Each row opens its
 * dashboard.
 */
import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { type Batch, listBatches } from './importApi';
import { TYPE_LABEL } from '../guideListQuery';
import './import.scss';
import { formatDateTime } from '@helpers/date';

const PAGE_SIZE = 25;
/*
 * Counts, totals and cost are not columns: the backend's batch list (GuideImportBatch) does not
 * carry them, and asking for them is what made this page a 500. They live on the batch dashboard
 * (getGuideImportBatchSummary), which every row opens. State is derived from canceledAt.
 */
const BATCH_COLUMNS = ['label', 'pageType', 'createdBy', 'createdAt', 'state'];
const BATCH_COLUMN_LAYOUT: ColumnsConfig = {
  label: { card: 'title', tablet: true },
  pageType: { card: 'subtitle', tablet: true },
  state: { card: 'status', tablet: true },
};

const formatDate = (iso?: string): string => {
  if (!iso) return '-';
  return formatDateTime(iso, iso);
};

const BatchesList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Batch[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  /* Bumped by "Try again". It used to call setPage((p) => p), which React bails out of because
     the state is unchanged, so the button did nothing at all. */
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    listBatches({ page, size: PAGE_SIZE })
      .then((data) => {
        if (cancelled) return;
        setRows(Array.isArray(data.content) ? data.content : []);
        setTotal(Number(data.totalElements) || 0);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.error || 'Failed to load batches');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, reloadKey]);

  useEffect(() => load(), [load]);

  return (
    <CRMPageTemplate
      icon={<Inventory2OutlinedIcon />}
      title="Import batches"
      totalCount={total}
      onAddNew={() => navigate('/guides/import')}
      addNewLabel="New import"
    >
      <Box className="guide-import-batches">
        {error ? (
          <Alert severity="error" action={<Button size="small" onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}>
            {error}
          </Alert>
        ) : !loading && rows.length === 0 ? (
          <Box className="guide-import-batches__empty">
            <Typography color="text.secondary" variant="body2">
              No import batches yet. Start one from the New import button.
            </Typography>
          </Box>
        ) : (
          <MintlyTable
            loading={loading}
            rows={rows.map((b) => ({ ...b, id: b.batchId }))}
            selectors={BATCH_COLUMNS}
            columns={BATCH_COLUMN_LAYOUT}
            getRowKey={(b: Batch) => b.batchId}
            onRowClick={(b: Batch) => navigate(`/guides/import/batches/${b.batchId}`)}
            pagination={{
              enabled: total > PAGE_SIZE,
              page,
              rowsPerPage: PAGE_SIZE,
              totalNumberOfRows: total,
              handleChangePage: (_e: unknown, p: number) => setPage(p),
            }}
            tableHeadRow={{
              label: 'Batch',
              pageType: 'Type',
              createdBy: 'Created by',
              createdAt: 'Created',
              state: 'State',
            }}
            tableBodyRow={{
              label: (b: Batch) => (
                <Box className="guide-import-batches__label">
                  <strong>{b.label || 'Untitled batch'}</strong>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {b.batchId}
                  </Typography>
                </Box>
              ),
              pageType: (b: Batch) => <>{TYPE_LABEL[b.pageType] || b.pageType}</>,
              createdBy: (b: Batch) => <>{b.createdBy || '-'}</>,
              createdAt: (b: Batch) => <>{formatDate(b.createdAt)}</>,
              state: (b: Batch) =>
                b.canceledAt ? (
                  <Chip size="small" variant="outlined" label="Canceled" className="guide-import-batches__state tone-neutral" />
                ) : (
                  <Chip size="small" variant="outlined" label="Active" className="guide-import-batches__state tone-success" />
                ),
            }}
          />
        )}
      </Box>
    </CRMPageTemplate>
  );
};

export default BatchesList;
